import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiPost } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BUDDY_IDS, BUDDY_ROSTER, BuddyAvatar, isBuddyUnlocked } from './BuddyRoster';
import { Lock, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * RosterPicker — the "Meet my friends" modal.
 * Shows all 4 buddies. Locked ones are dimmed and route to /subscription on click.
 */
export default function RosterPicker({ open, onOpenChange, onPick }) {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(user?.preferred_buddy || 'chirpy');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setActive(user?.preferred_buddy || 'chirpy'); }, [user?.preferred_buddy]);

  const tier = (user?.subscription_tier || 'basic').toLowerCase();
  const role = (user?.role || 'student').toLowerCase();

  const handlePick = async (id) => {
    if (!isBuddyUnlocked(id, tier, role)) {
      navigate('/subscription');
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      await apiPost('/users/preferred-buddy', { buddy_id: id });
      setActive(id);
      onPick?.(id);
      await checkAuth?.();
      toast.success(`${BUDDY_ROSTER[id].name} is now your study buddy!`);
      onOpenChange(false);
    } catch {
      toast.error('Could not switch buddy. Try again.');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl rounded-3xl" data-testid="roster-picker">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-slate-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" /> Meet your study buddies
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 font-body -mt-2 mb-3">Each one brings a different vibe. Pick the friend who matches your mood today.</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {BUDDY_IDS.map(id => {
            const b = BUDDY_ROSTER[id];
            const unlocked = isBuddyUnlocked(id, tier, role);
            const isActive = active === id;
            const isRive = b?.kind === 'rive';
            return (
              <button key={id}
                data-testid={`roster-card-${id}`}
                onClick={() => handlePick(id)}
                disabled={saving}
                className={`relative p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 text-center overflow-hidden ${
                  isActive ? 'border-emerald-400 bg-emerald-50 scale-[1.02] shadow-md' :
                  unlocked ? (isRive ? 'border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white hover:border-indigo-400 hover:scale-[1.02]'
                                     : 'border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/30 hover:scale-[1.02]') :
                  'border-slate-200 bg-slate-50 opacity-75 hover:opacity-100'
                }`}
              >
                {b.badge && (
                  <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-700 text-[8px] font-heading uppercase tracking-wider">
                    {b.badge}
                  </span>
                )}
                <div className={`w-32 h-32 sm:w-36 sm:h-36 rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50 to-rose-50 ring-1 ring-slate-100 ${unlocked ? '' : 'grayscale'}`}>
                  <BuddyAvatar buddyId={id} size={144} mood={isActive ? 'happy' : 'idle'} />
                </div>
                {!unlocked && (
                  <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-amber-400 ring-2 ring-white flex items-center justify-center shadow-md">
                    <Lock className="w-3.5 h-3.5 text-white" />
                  </span>
                )}
                {isActive && (
                  <span className="absolute top-3 left-3 w-7 h-7 rounded-full bg-emerald-500 ring-2 ring-white flex items-center justify-center shadow-md" data-testid={`roster-active-${id}`}>
                    <Check className="w-3.5 h-3.5 text-white" />
                  </span>
                )}
                <div className="mt-1">
                  <p className="font-heading text-base font-bold text-slate-800">{b.name}</p>
                  <p className="text-[11px] text-slate-500 font-body line-clamp-2 leading-tight mt-0.5">{b.tagline}</p>
                </div>
                {!unlocked && (
                  <span className="text-[10px] text-amber-600 font-heading uppercase tracking-wider">
                    {b.unlock_tier === 'basic_lifetime' ? 'Basic Lifetime' : (b.unlock_tier === 'growth' ? 'Growth' : 'Elite')}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="text-xs text-slate-400 font-body text-center mt-3">
          Locked friends? <Button variant="link" className="text-emerald-600 h-auto p-0 text-xs font-heading" onClick={() => { navigate('/subscription'); onOpenChange(false); }}>Upgrade to unlock all</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
